/**
 * Drop-in replacement for react-query's useQuery with global AbortError handling.
 *
 * - Filters AbortError from error state so cancelled requests don't show as errors
 * - If a still-mounted observer aborts with no data, keep loading + refetch
 *   (mends connection after navigation / auth races)
 * - React Query automatically passes AbortSignal to your queryFn when it runs
 * - Use this instead of useQuery for data-fetching to get global abort safety
 */
import { useEffect, useRef } from "react";
import { useQuery as useReactQuery, UseQueryResult } from "react-query";
import { isAbortError } from "../errors/isAbortError";
import {
  SUPABASE_ABORT_RETRY_ATTEMPTS,
  abortRetryDelayMs,
} from "../errors/supabaseAbortRetry";

// Re-export types for convenience
export type { UseQueryOptions, UseQueryResult } from "react-query";

/**
 * useQuery with global AbortError handling.
 * Aborted/cancelled requests will NOT surface as isError/error in the UI.
 *
 * Your queryFn receives { queryKey, signal } - pass signal to Supabase for cancellation:
 *   useQuery(key, async ({ signal }) => {
 *     return await supabase.from('x').select().abortSignal(signal);
 *   }, options)
 */
export function useQuery(...args: Parameters<typeof useReactQuery>): UseQueryResult<any, any> {
  const result = useReactQuery(...args);

  // Treat AbortError as "cancelled" - don't expose as error to consumers
  const error = result.error;
  const isAborted = error != null && isAbortError(error);
  const abortedWithNoData = isAborted && result.data === undefined;
  const abortRetriesRef = useRef(0);

  // Still-mounted observer + abort + no cache → refetch so pages remend the
  // Supabase connection instead of sticking on skeletons.
  useEffect(() => {
    if (result.fetchStatus === "fetching") return;
    if (!abortedWithNoData) {
      abortRetriesRef.current = 0;
      return;
    }
    if (abortRetriesRef.current >= SUPABASE_ABORT_RETRY_ATTEMPTS) return;
    abortRetriesRef.current += 1;
    const attempt = abortRetriesRef.current;
    const t = window.setTimeout(() => {
      void result.refetch();
    }, abortRetryDelayMs(attempt));
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to abort settle
  }, [abortedWithNoData, result.fetchStatus]);

  return {
    ...result,
    error: isAborted ? undefined : result.error,
    isError: result.isError && !isAborted,
    // Keep consumers in a loading state until a non-abort result arrives
    isLoading: result.isLoading || abortedWithNoData,
    isFetching: result.isFetching || abortedWithNoData,
  } as UseQueryResult<any, any>;
}
